import type { PreSignedFile } from '~/models/Media'
import type { FileForm } from '~/models/FileForm'
import { ref } from 'vue'
import uniqId from 'uniqid'
import { storeToRefs } from 'pinia'
import axios from 'axios'
import useAuthStore from '~/stores/auth'
import { useFileUploadApi } from '~/api/fileUploadApi'

const MAX_FILE_SIZE_MB = 100
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024
const MAX_FILE_NAME = 80
const MIN_FILE_NAME = 3

export default () => {
  const { userId } = storeToRefs(useAuthStore())
  const { createFileUploadForm, createPreSignedFile } = useFileUploadApi()

  const isUploading = ref(false)
  const isDownloading = ref(false)
  const fileTooLarge = ref(false)
  const forceHost: string | undefined = import.meta.env.VITE_USER_MEDIA_HOST

  // eslint-disable-next-line no-template-curly-in-string
  const createFileKey = () => `signed/${userId.value}/${uniqId()}/` + '${filename}'

  const getFormAction = (form: FileForm) =>
    forceHost ? `https://${forceHost}` : form.attributes.action

  async function postUploadFile(file: File): Promise<PreSignedFile> {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      fileTooLarge.value = true
      throw new Error('File is too large')
    }
    isUploading.value = true
    fileTooLarge.value = false
    return createFileUploadForm()
      .then(async (form) => {
        const fileKey = createFileKey()
        const formData = new FormData()
        Object.keys(form.inputs)
          .forEach(name => formData.append(name, form.inputs[name]))
        formData.set('key', fileKey)
        formData.append('file', file)
        return axios.post(getFormAction(form), formData, {
          headers: { 'Content-Type': <string>form.attributes.enctype },
        }).then(() => ({
          name: file.name,
          // eslint-disable-next-line no-template-curly-in-string
          key: fileKey.replace('${filename}', file.name),
          mimeType: file.type,
          fileSize: file.size,
        })).finally(() => {
          isUploading.value = false
        })
      })
  }

  async function downloadFileKey(fileKey: string) {
    isDownloading.value = true
    return createPreSignedFile(fileKey)
      .then(({ uri }) => {
        const link = document.createElement('a')
        link.href = uri
        link.target = '_tab'
        link.click()
        link.remove()
      })
      .finally(() => isDownloading.value = false)
  }

  return {
    isUploading,
    isDownloading,
    fileTooLarge,
    postUploadFile,
    downloadFileKey,
    MAX_FILE_SIZE_MB,
    MAX_FILE_NAME,
    MIN_FILE_NAME,
  }
}
